import { alpha, createTheme, ThemeProvider } from "@mui/material/styles";
import { ReactNode } from "react";
import useSkpTheme from "../../hooks/useSkpTheme";
import { cssVar } from "../../utils/cssVar";
import { plex } from "../../utils/fonts";

type MuiThemeProviderProp = {
  children: ReactNode;
};

export const MuiThemeProvider = (
  muiThemeProviderProp: MuiThemeProviderProp
) => {
  useSkpTheme();

  const muiTheme = createTheme({
    palette: {
      primary: {
        main: cssVar("--primary-color"),
      },
      warning: {
        main: cssVar("--warning-color"),
      },
    },
    typography: {
      // MUI components fell back to the default Roboto stack while the rest of
      // the app renders IBM Plex Sans (see the `body` rule in index.css), which
      // is what made the mailbox's fixed fields look different from the
      // structured ones. Only the font stack is aligned here; per-input
      // styling stays with the components that need it.
      fontFamily: plex,
      button: {
        textTransform: "none",
      },
    },
    components: {
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
          },
        },
      },
      // ============================ Locale Switcher ==================================================================
      MuiSelect: {
        variants: [
          {
            props: { localeSwitcher: true },
            style: {
              "& .MuiSelect-select": {
                color: "var(--locale-switcher-text-color)",
                fontSize: "var(--locale-switcher-font-size)",
                fontWeight: "bold",
                "&:hover": { filter: "brightness(0.85)" },
              },
              "& .MuiSelect-icon": {
                color: "var(--locale-switcher-text-color)",
              },
            },
          },
        ],
      },
      MuiOutlinedInput: {
        variants: [
          {
            // this matches when you pass <OutlinedInput localeSwitcher />
            props: { localeSwitcher: true },
            style: {
              /* Outline colors */
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--locale-switcher-border-color)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor:
                  "var(--locale-switcher-hover-border-color) !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor:
                  "var(--locale-switcher-hover-border-color) !important",
              },

              /* Select text + icon that live inside OutlinedInput */
              "& .MuiSelect-select": {
                color: "var(--locale-switcher-text-color)",
                fontSize: "var(--locale-switcher-font-size)",
                fontWeight: "bold",
                "&:hover": { filter: "brightness(0.85)" },
              },
              "& .MuiSelect-icon": {
                color: "var(--locale-switcher-text-color)",
              },
            },
          },
        ],
      },
      // ================================================================================================================

      // ============================ Buttons ===========================================================================
      MuiButton: {
        styleOverrides: {
          outlined: {
            borderRadius: 20,
          },
          // Override text variant of Mui button [OptionsButton].
          text: {
            color: "var(--options-button-text-color)", // Color for text inside button.
            fontSize: "var(--options-button-text-font-size)", // Font size for text inside button.
          },
          // Override contained variant of Mui button [SendButton].
          contained: {
            color: "white",
            borderRadius: "var(--send-button-border-radius)",
            paddingY: "var(--send-button-vertical-padding)",
            paddingX: "var(--send-button-horizontal-padding)",
            textTransform: "none",
            ":hover": {
              backgroundColor: "var(--send-button-hover-bg-color)",
              filter: "brightness(0.95)",
            },
          },
        },
      },
      // ================================================================================================================

      // ============================ Text Field ========================================================================
      // Override text variant of Mui TextField.
      MuiTextField: {
        defaultProps: {
          variant: "filled",
        },
      },
      MuiFilledInput: {
        defaultProps: {
          disableUnderline: true,
        },

        styleOverrides: {
          root: {
            backgroundColor: "#F7F7F7",
            borderRadius: "3px",
          },
          adornedEnd: {
            backgroundColor: "#F7F7F7",
          },
        },
      },
      // ================================================================================================================
      MuiCard: {
        variants: [
          {
            props: { variant: "absentUser" },
            style: ({ theme }) => ({
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 14,
              backgroundColor: "white",
              boxShadow: `0 8px 24px ${alpha(
                theme.palette.common.black,
                0.08
              )}`,
              transition: theme.transitions.create(
                ["box-shadow", "transform"],
                {
                  duration: theme.transitions.duration.shorter,
                }
              ),
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 12px 32px ${alpha(
                  theme.palette.common.black,
                  0.12
                )}`,
              },
            }),
          },
        ],
      },
    },
  });

  return (
    <ThemeProvider theme={muiTheme}>
      {muiThemeProviderProp.children}
    </ThemeProvider>
  );
};
